import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Trash2, Plus, Zap, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VotifierManagerProps {
  serverId: string;
  serverName: string;
  gameType?: string;
}

interface VotifierConfig {
  id: string;
  server_id: string;
  host: string;
  port: number;
  token: string;
  protocol: 'v1' | 'v2';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const VotifierManager: React.FC<VotifierManagerProps> = ({ 
  serverId, 
  serverName, 
  gameType 
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [newConfig, setNewConfig] = useState({
    host: '',
    port: 8192,
    token: '',
    protocol: 'v2' as 'v1' | 'v2'
  });

  // Vérifier si c'est un serveur Minecraft
  const isMinecraftServer = gameType?.toLowerCase().includes('minecraft') || 
                           serverName.toLowerCase().includes('minecraft');

  const { data: configs, isLoading } = useQuery({
    queryKey: ['votifier-configs', serverId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('votifier_configs')
        .select('*')
        .eq('server_id', serverId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!serverId && !!user && isMinecraftServer,
  });

  const createConfigMutation = useMutation({
    mutationFn: async (config: typeof newConfig) => {
      const { data, error } = await supabase
        .from('votifier_configs')
        .insert({
          server_id: serverId,
          host: config.host,
          port: config.port,
          token: config.token,
          protocol: config.protocol,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['votifier-configs', serverId] });
      setNewConfig({ host: '', port: 8192, token: '', protocol: 'v2' });
      toast({
        title: t('votifier.configCreated'),
        description: t('votifier.configCreatedDesc'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('votifier.error'),
        description: error.message || t('votifier.createError'),
        variant: 'destructive',
      });
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('votifier_configs')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['votifier-configs', serverId] });
      toast({
        title: t('votifier.configUpdated'),
        description: t('votifier.configUpdatedDesc'),
      });
    },
  });

  const deleteConfigMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('votifier_configs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['votifier-configs', serverId] });
      toast({
        title: t('votifier.configDeleted'),
        description: t('votifier.configDeletedDesc'),
      });
    },
  });

  const generateToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewConfig({ ...newConfig, token: result });
  };

  const handleCreateConfig = () => {
    if (!newConfig.host || !newConfig.token || !newConfig.port) {
      toast({
        title: t('votifier.requiredFields'),
        description: t('votifier.requiredFieldsDesc'),
        variant: 'destructive',
      });
      return;
    }

    if (newConfig.port < 1 || newConfig.port > 65535) {
      toast({
        title: t('votifier.invalidPort'),
        description: t('votifier.invalidPortDesc'),
        variant: 'destructive',
      });
      return;
    }

    createConfigMutation.mutate(newConfig);
  };

  // Si ce n'est pas un serveur Minecraft, ne pas afficher le composant
  if (!isMinecraftServer) {
    return null;
  }

  if (isLoading) {
    return <div className="text-center p-4">{t('votifier.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-400" />
            {t('votifier.title')} - {serverName}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Alerte d'information */}
          <Alert className="bg-blue-900/20 border-blue-600/50">
            <AlertCircle className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-blue-200">
              <strong>Votifier</strong> {t('votifier.description')}
            </AlertDescription>
          </Alert>

          {/* Liste des configurations existantes */}
          {configs && configs.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-white">{t('votifier.activeConfigs')}</h4>
              {configs.map((config) => (
                <Card key={config.id} className="bg-slate-700 border-slate-600">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <code className="text-sm bg-slate-600 px-2 py-1 rounded text-slate-200">
                            {config.host}:{config.port}
                          </code>
                          <Badge 
                            variant={config.protocol === 'v2' ? 'default' : 'secondary'} 
                            className="text-xs"
                          >
                            {config.protocol.toUpperCase()}
                          </Badge>
                          <Switch
                            checked={config.is_active}
                            onCheckedChange={(checked) =>
                              updateConfigMutation.mutate({ id: config.id, is_active: checked })
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={config.is_active ? 'default' : 'secondary'} className="text-xs">
                            {config.is_active ? (
                              <><CheckCircle className="w-3 h-3 mr-1" />{t('votifier.active')}</>
                            ) : (
                              <>{t('votifier.inactive')}</>
                            )}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            Token: {config.token.substring(0, 8)}***
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteConfigMutation.mutate(config.id)}
                        disabled={deleteConfigMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Separator className="bg-slate-600" />

          {/* Formulaire d'ajout */}
          <div className="space-y-4">
            <h4 className="font-medium text-white flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t('votifier.addConfig')}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="votifier-host" className="text-slate-300">
                  {t('votifier.serverHost')}
                </Label>
                <Input
                  id="votifier-host"
                  type="text"
                  placeholder={t('votifier.serverHostPlaceholder')}
                  value={newConfig.host}
                  onChange={(e) => setNewConfig({ ...newConfig, host: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-slate-200"
                />
              </div>
              <div>
                <Label htmlFor="votifier-port" className="text-slate-300">
                  {t('votifier.port')}
                </Label>
                <Input
                  id="votifier-port"
                  type="number"
                  min="1"
                  max="65535"
                  placeholder={t('votifier.portPlaceholder')}
                  value={newConfig.port}
                  onChange={(e) => setNewConfig({ ...newConfig, port: parseInt(e.target.value) || 8192 })}
                  className="bg-slate-700 border-slate-600 text-slate-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="votifier-protocol" className="text-slate-300">
                  {t('votifier.protocol')}
                </Label>
                <Select
                  value={newConfig.protocol}
                  onValueChange={(value: 'v1' | 'v2') => setNewConfig({ ...newConfig, protocol: value })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="v2" className="text-slate-200">
                      {t('votifier.protocolV2')}
                    </SelectItem>
                    <SelectItem value="v1" className="text-slate-200">
                      {t('votifier.protocolV1')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="votifier-token" className="text-slate-300">
                  {t('votifier.token')}
                </Label>
                <div className="flex gap-2 min-w-0">
                  <Input
                    id="votifier-token"
                    type="text"
                    placeholder={t('votifier.tokenPlaceholder')}
                    value={newConfig.token}
                    onChange={(e) => setNewConfig({ ...newConfig, token: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-slate-200 flex-1 min-w-0"
                  />
                  <Button type="button" variant="outline" onClick={generateToken} className="shrink-0">
                    {t('votifier.generate')}
                  </Button>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCreateConfig}
              disabled={createConfigMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createConfigMutation.isPending ? t('votifier.creating') : t('votifier.createConfig')}
            </Button>
          </div>

          {/* Documentation */}
          <Separator className="bg-slate-600" />
          <div className="space-y-3">
            <h4 className="font-medium text-white">{t('votifier.installationGuide')}</h4>
            <div className="text-sm text-slate-300 space-y-2">
              <p><strong>{t('votifier.pluginInstall')}</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>{t('votifier.installStep1')}</li>
                <li>{t('votifier.installStep2')}</li>
                <li>{t('votifier.installStep3')}</li>
                <li>{t('votifier.installStep4')}</li>
                <li>{t('votifier.installStep5')}</li>
              </ol>
              
              <p><strong>{t('votifier.pluginConfig')}</strong></p>
              <pre className="bg-slate-900 p-3 rounded text-xs overflow-x-auto">
{`host: 0.0.0.0  # Écouter sur toutes les interfaces
port: ${newConfig.port}  # Port configuré ci-dessus
debug: false
${newConfig.protocol === 'v2' ? `# Pour Votifier v2
token: ${newConfig.token || 'votre-token-ici'}` : `# Pour Votifier v1
rsa-key: |
  -----BEGIN RSA PRIVATE KEY-----
  [Votre clé RSA générée par le plugin]
  -----END RSA PRIVATE KEY-----`}
`}
              </pre>

              <Alert className="bg-yellow-900/20 border-yellow-600/50">
                <AlertCircle className="h-4 w-4 text-yellow-400" />
                <AlertDescription className="text-yellow-200">
                  <strong>{t('votifier.importantNote')}</strong> {t('votifier.firewallNote', { port: newConfig.port })}
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};